import React, { useState, useEffect, useCallback, Fragment } from 'react'
import { useNavigate } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import styled from 'styled-components'
import { format, formatISO, parseISO, addDays, isSameDay, addWeeks, subWeeks } from 'date-fns'

import { TextInput } from '../../../../components/ui-elements/TextInput'
import { Button } from '../../../../components/ui-elements/Button'
import { CheckBox } from '../../../../components/ui-elements/CheckBox'
import { LoadingSpinner } from '../../../../components/ui-elements/LoadingSpinner'
import { useModal } from '../../../../hooks/useModal'
import { Fetch } from '../../../../function/Fetch'
import {
  FormProps,
  FormContainerProps,
  FormType,
  ConfigType,
  SchedulableDateResponse,
  SchedulableTime,
  CustomPageItemType,
} from './Form.types'
import { useConfig } from '../../../../routes'
import {
  FieldWrapper,
  validationRules,
  TextInputField,
  SingleSelectField,
  TextareaField,
  NumberInputField,
  RadioInputField,
  DateInputField,
  MultiSelectField,
  MonthInputField,
  NameInputField,
} from '../../../../components/ui-parts/Form'
import { styleTheme } from '../../../../style/theme'
import { SchedulableTimes } from './SchedulableTimes'

export const Form: React.FC<FormContainerProps> = (props) => {
  const navigate = useNavigate()
  const { facilities, reservationPageItems } = useConfig() as ConfigType
  const {
    register,
    control,
    handleSubmit,
    watch,
    getValues,
    setValue,
    formState: { errors, isSubmitting },
  } = useForm<FormType>({
    defaultValues: {
      facilityId: facilities[0].id,
    },
  })

  const onSubmit = handleSubmit(async (data) => {
    if (!data.reservationAt) return
    const body = {
      reservation_page_id: props.reservationPageKey,
      reservation_at: formatISO(data.reservationAt),
      email: data.email,
      last_name: data.lastName,
      first_name: data.firstName,
      reservation_select_item_id:
        data.reservationSelectItemId ||
        facilities.find((facility) => facility.id === data.facilityId)?.reservationSelectItems[0]?.id,
      reservation_type: props.selectType,
      reservation_infos_attributes: reservationPageItems
        .filter((pageItem): pageItem is CustomPageItemType => pageItem.fieldType === 'reservation_item')
        .map((pageItem) => ({
          reservation_item_id: pageItem.reservationItem.id,
          name: pageItem.name,
          order: pageItem.order,
          item_key: pageItem.reservationItem.itemKey,
          input_value: data.reservationInfos?.[pageItem.reservationItem.id],
        })),
    }
    if (reservationPageItems.some((item) => item.fieldType === 'name_kana')) {
      body['last_name_kana'] = data.lastNameKana
      body['first_name_kana'] = data.firstNameKana
    }
    if (reservationPageItems.some((item) => item.fieldType === 'gender')) {
      body['gender'] = data.gender
    }
    if (reservationPageItems.some((item) => item.fieldType === 'phone_number')) {
      body['phone_number'] = data.phoneNumber
    }
    if (reservationPageItems.some((item) => item.fieldType === 'number_of_people')) {
      body['number_of_people'] = data.numberOfPeople
    }
    if (reservationPageItems.some((item) => item.fieldType === 'content')) {
      body['content'] = data.content
    }
    const res = await Fetch(`/customer/api/v1/facilities/${data.facilityId}/appointments`, 'POST', {
      appointment: body,
    })

    if (res.ok) {
      navigate(`/customer/public/reservation_pages/${props.reservationPageKey}/thanks_appointment`)
      return
    } else {
      alert('予約できませんでした。時間枠を変更して再度予約をお願いします。')
      return
    }
  })

  return (
    <StyledComponent
      {...props}
      register={register}
      control={control}
      onSubmit={onSubmit}
      errors={errors}
      watch={watch}
      getValues={getValues}
      setValue={setValue}
      isSending={isSubmitting}
      reservationPageItems={reservationPageItems}
    />
  )
}

const Component: React.FC<FormProps> = ({
  selectType,
  className,
  register,
  control,
  onSubmit,
  errors,
  watch,
  setValue,
  isSending,
  reservationPageItems,
}) => {
  const config = useConfig() as ConfigType
  const primaryColor = config.primaryColor
  const watchReservationAt = watch('reservationAt')
  const watchFacilityId = watch('facilityId')
  const watchEmail = watch('email')
  const selectedFacility = config.facilities.find((v) => v.id === watchFacilityId)
  const [isCheckedAgreement, setIsCheckedAgreement] = useState(false)
  const [schedulableTimes, setSchedulableTimes] = useState<{
    [key: string]: SchedulableTime[]
  }>({})
  const { Modal, open, close } = useModal()
  const [scheduleFetching, setScheduleFetching] = useState(false)

  const [currentDate, setCurrentDate] = useState<Date>(new Date())

  const shiftScheduleDate = useCallback(
    (arg: 'prev' | 'next') => {
      if (arg === 'prev') {
        const today = new Date()
        if (isSameDay(today, currentDate)) {
          return
        }
        const newDate = subWeeks(currentDate, 1)
        setCurrentDate(newDate)
      } else {
        const newDate = addWeeks(currentDate, 1)
        setCurrentDate(newDate)
      }
    },
    [currentDate],
  )

  const refresh = useCallback(async () => {
    const apiUrl = `/customer/api/v1/facilities/${watchFacilityId}/schedulable_times?type=${selectType}&date=${format(currentDate, 'yyyy-MM-dd')}`
    setScheduleFetching(true)
    const res = await fetch(apiUrl)
    const json = (await res.json()) as SchedulableDateResponse
    const newSchedulableTimes: { [key: string]: SchedulableTime[] } = {}
    json.schedulable_times.forEach((schedulableTime) => {
      const key = format(schedulableTime.date, 'yyyyMMdd')
      if (!newSchedulableTimes[key]) {
        newSchedulableTimes[key] = []
      }
      schedulableTime.times.forEach((time) => {
        const startAt = parseISO(time.start_at)
        const endAt = parseISO(time.end_at)
        newSchedulableTimes[key].push({
          startAt: startAt,
          endAt: endAt,
          isSchedulable: time.is_schedulable,
        })
      })
    })
    setScheduleFetching(false)
    setSchedulableTimes(newSchedulableTimes)
  }, [selectType, currentDate, watchFacilityId])

  useEffect(() => {
    refresh()
  }, [currentDate, refresh])

  useEffect(() => {
    setValue('reservationAt', null)
  }, [watchFacilityId, setValue])

  const schedules = [...Array(7)].map((_, i) => {
    const date = addDays(currentDate, i)
    const key = format(date, 'yyyyMMdd')
    const times = schedulableTimes[key] || []

    if (times.length > 0) {
      return (
        <SchedulableTimes
          key={key}
          date={date}
          schedulableTimes={times}
          onChange={refresh}
          facilityId={watchFacilityId}
          meetingType={selectType}
          chooseSchedule={(value) => setValue('reservationAt', value.startAt)}
          onClick={close}
          primaryColor={primaryColor}
        />
      )
    } else {
      return null
    }
  })

  return (
    <form className={className} onSubmit={onSubmit}>
      <Modal
        title="ご希望の日時"
        submit={() => { }}
        cancelText="キャンセル"
        submitText="日時を選択"
        primaryColor={primaryColor}
      >
        <div className={`${className}__modal-contents`}>
          {!scheduleFetching && (
            <>
              <div className={`${className}__modal-date-change`}>
                <Button color={primaryColor} invert onClick={() => shiftScheduleDate('prev')}>
                  {'< 前の週'}
                </Button>
                <Button color={primaryColor} invert onClick={() => shiftScheduleDate('next')}>
                  {'次の週 >'}
                </Button>
              </div>
              {schedules}
            </>
          )}
          {scheduleFetching && <LoadingSpinner primaryColor={primaryColor} />}
        </div>
      </Modal>

      {reservationPageItems.map((reservationPageItem, itemIndex) => {
        switch (reservationPageItem.fieldType) {
          case 'reservation_at':
            return (
              <FieldWrapper
                key={`${reservationPageItem.fieldType}.${reservationPageItem.order}.${itemIndex}`}
                label={reservationPageItem.name}
                error={errors['reservationAt']}
                isRequired
              >
                <TextInput
                  {...register('reservationAt', { required: validationRules.required(reservationPageItem.name) })}
                  type="text"
                  value={watchReservationAt instanceof Date ? format(watchReservationAt, 'yyyy年MM月dd日 HH:mm') : ''}
                  placeholder={reservationPageItem.placeholder}
                  onClick={() => open()}
                  readOnly
                  style={{ cursor: 'pointer', width: 'auto' }}
                  className={`reservation-at__text-input ${errors['reservationAt'] ? 'error' : ''}`}
                />
              </FieldWrapper>
            )
          case 'facility':
            return config.facilities.length > 1 ? (
              <SingleSelectField
                key={`${reservationPageItem.fieldType}.${reservationPageItem.order}.${itemIndex}`}
                label={reservationPageItem.name}
                control={control}
                name="facilityId"
                options={config.facilities.map((facility) => ({
                  label: facility.name,
                  value: facility.id,
                }))}
                placeholder={reservationPageItem.placeholder}
                error={errors['facilityId']}
                required
              />
            ) : null
          case 'reservation_select_item':
            return selectedFacility && selectedFacility.reservationSelectItems.length > 1 ? (
              <SingleSelectField
                key={`${reservationPageItem.fieldType}.${reservationPageItem.order}.${itemIndex}`}
                control={control}
                label={reservationPageItem.name}
                name="reservationSelectItemId"
                options={selectedFacility.reservationSelectItems.map((reservationSelectItem) => ({
                  label: reservationSelectItem.name,
                  value: reservationSelectItem.id,
                }))}
                placeholder={reservationPageItem.placeholder}
                error={errors['reservationSelectItemId']}
                required
              />
            ) : null
          case 'name':
            return (
              <NameInputField
                key={`${reservationPageItem.fieldType}.${reservationPageItem.order}.${itemIndex}`}
                register={register}
                label={reservationPageItem.name}
                placeholders={{ lastName: '姓', firstName: '名' }}
                errors={errors}
                required
              />
            )
          case 'name_kana':
            return (
              <NameInputField
                key={`${reservationPageItem.fieldType}.${reservationPageItem.order}.${itemIndex}`}
                register={register}
                label={reservationPageItem.name}
                names={{ lastName: 'lastNameKana', firstName: 'firstNameKana' }}
                placeholders={{ lastName: 'せい', firstName: 'めい' }}
                errors={errors}
                required={reservationPageItem.isRequired}
                pattern="hiraganaOrKatakana"
              />
            )
          case 'gender':
            const genderOptions = [
              { label: '女性', value: 'female' },
              { label: '男性', value: 'male' },
            ]
            if (!reservationPageItem.isRequired) {
              genderOptions.push({ label: 'その他', value: 'other' })
            }
            return (
              <RadioInputField<FormType>
                key={`${reservationPageItem.fieldType}.${reservationPageItem.order}.${itemIndex}`}
                label={reservationPageItem.name}
                name="gender"
                register={register}
                radioOptions={genderOptions}
                error={errors['gender']}
                required={reservationPageItem.isRequired}
              />
            )
          case 'email':
            return (
              <Fragment key={`${reservationPageItem.fieldType}.${reservationPageItem.order}.${itemIndex}`}>
                <TextInputField<FormType>
                  label={reservationPageItem.name}
                  name="email"
                  type="email"
                  pattern="email"
                  register={register}
                  placeholder={reservationPageItem.placeholder}
                  error={errors['email']}
                  required
                />
                <TextInputField<FormType>
                  label={`${reservationPageItem.name}（確認）`}
                  name="emailConfirmation"
                  type="email"
                  pattern="email"
                  register={register}
                  placeholder={reservationPageItem.placeholder}
                  error={errors['emailConfirmation']}
                  validate={(value) => {
                    return value === watchEmail || 'メールアドレスが一致しません'
                  }}
                  required
                />
              </Fragment>
            )
          case 'phone_number':
            return (
              <TextInputField<FormType>
                key={`${reservationPageItem.fieldType}.${reservationPageItem.order}.${itemIndex}`}
                label={reservationPageItem.name}
                name="phoneNumber"
                type="text"
                pattern="phoneNumber"
                register={register}
                placeholder={reservationPageItem.placeholder}
                error={errors['phoneNumber']}
                required={reservationPageItem.isRequired}
              />
            )
          case 'number_of_people':
            return (
              <SingleSelectField
                key={`${reservationPageItem.fieldType}.${reservationPageItem.order}.${itemIndex}`}
                label={reservationPageItem.name}
                name="numberOfPeople"
                control={control}
                options={[
                  {
                    value: '1',
                    label: '1人',
                  },
                  {
                    value: '2',
                    label: '2人',
                  },
                  {
                    value: '3',
                    label: '3人',
                  },
                  {
                    value: '4',
                    label: '4人',
                  },
                ]}
                error={errors['numberOfPeople']}
                required={reservationPageItem.isRequired}
                placeholder={reservationPageItem.placeholder}
                isSearchable={false}
              />
            )
          case 'content':
            return (
              <TextareaField
                key={`${reservationPageItem.fieldType}.${reservationPageItem.order}.${itemIndex}`}
                label={reservationPageItem.name}
                name="content"
                placeholder={reservationPageItem.placeholder}
                rows={4}
                register={register}
                error={errors['content']}
                required={reservationPageItem.isRequired}
              />
            )
          case 'reservation_item':
            switch (reservationPageItem.reservationItem.inputType) {
              case 'string':
                return (
                  <TextInputField<FormType>
                    key={`${reservationPageItem.fieldType}.${reservationPageItem.reservationItem.id}`}
                    label={reservationPageItem.name}
                    name={`reservationInfos.${reservationPageItem.reservationItem.id}`}
                    type="text"
                    register={register}
                    placeholder={reservationPageItem.placeholder}
                    error={
                      errors['reservationInfos']
                        ? errors['reservationInfos'][reservationPageItem.reservationItem.id]
                        : null
                    }
                    required={reservationPageItem.isRequired}
                  />
                )
              case 'select':
                return (
                  <SingleSelectField
                    key={`${reservationPageItem.fieldType}.${reservationPageItem.reservationItem.id}`}
                    label={reservationPageItem.name}
                    control={control}
                    name={`reservationInfos.${reservationPageItem.reservationItem.id}`}
                    options={
                      reservationPageItem.reservationItem?.selectItems?.map((selectItem) => ({
                        label: selectItem,
                        value: selectItem,
                      })) || []
                    }
                    placeholder={reservationPageItem.placeholder}
                    error={
                      errors['reservationInfos']
                        ? errors['reservationInfos'][reservationPageItem.reservationItem.id]
                        : null
                    }
                    required={reservationPageItem.isRequired}
                  />
                )
              case 'text':
                return (
                  <TextareaField<FormType>
                    key={`${reservationPageItem.fieldType}.${reservationPageItem.reservationItem.id}`}
                    label={reservationPageItem.name}
                    name={`reservationInfos.${reservationPageItem.reservationItem.id}`}
                    placeholder={reservationPageItem.placeholder}
                    rows={4}
                    register={register}
                    error={
                      errors['reservationInfos']
                        ? errors['reservationInfos'][reservationPageItem.reservationItem.id]
                        : null
                    }
                    required={reservationPageItem.isRequired}
                  />
                )
              case 'integer':
                return (
                  <NumberInputField<FormType>
                    key={`${reservationPageItem.fieldType}.${reservationPageItem.reservationItem.id}`}
                    label={reservationPageItem.name}
                    name={`reservationInfos.${reservationPageItem.reservationItem.id}`}
                    register={register}
                    placeholder={reservationPageItem.placeholder}
                    error={
                      errors['reservationInfos']
                        ? errors['reservationInfos'][reservationPageItem.reservationItem.id]
                        : null
                    }
                    required={reservationPageItem.isRequired}
                  />
                )
              case 'date':
                return (
                  <DateInputField
                    key={`${reservationPageItem.fieldType}.${reservationPageItem.reservationItem.id}`}
                    label={reservationPageItem.name}
                    name={`reservationInfos.${reservationPageItem.reservationItem.id}`}
                    control={control}
                    placeholder={reservationPageItem.placeholder}
                    error={
                      errors['reservationInfos']
                        ? errors['reservationInfos'][reservationPageItem.reservationItem.id]
                        : null
                    }
                    required={reservationPageItem.isRequired}
                  />
                )
              case 'month':
                return (
                  <MonthInputField
                    key={`${reservationPageItem.fieldType}.${reservationPageItem.reservationItem.id}`}
                    label={reservationPageItem.name}
                    name={`reservationInfos.${reservationPageItem.reservationItem.id}`}
                    control={control}
                    placeholder={reservationPageItem.placeholder}
                    error={
                      errors['reservationInfos']
                        ? errors['reservationInfos'][reservationPageItem.reservationItem.id]
                        : null
                    }
                    required={reservationPageItem.isRequired}
                  />
                )
              case 'multi_select':
                return (
                  <MultiSelectField
                    key={`${reservationPageItem.fieldType}.${reservationPageItem.reservationItem.id}`}
                    label={reservationPageItem.name}
                    control={control}
                    name={`reservationInfos.${reservationPageItem.reservationItem.id}`}
                    options={
                      reservationPageItem.reservationItem?.selectItems?.map((selectItem) => ({
                        label: selectItem,
                        value: selectItem,
                      })) || []
                    }
                    placeholder={reservationPageItem.placeholder}
                    error={
                      errors['reservationInfos']
                        ? errors['reservationInfos'][reservationPageItem.reservationItem.id]
                        : null
                    }
                    required={reservationPageItem.isRequired}
                  />
                )
              default:
                return null
            }
          default:
            return null
        }
      })}
      <div className="agreement">
        <CheckBox
          linkText="プライバシーポリシー"
          linkURL={config.tenant.privacyPolicyLink}
          label={`${config.tenant.privacyPolicyLink ? '' : 'プライバシーポリシー'}に同意する`}
          required
          checked={isCheckedAgreement}
          onChange={(e) => setIsCheckedAgreement(e.target.checked)}
        />
      </div>

      <div className="submit">
        <Button type="submit" color={primaryColor} disabled={!isCheckedAgreement || isSending}>
          予約する
        </Button>
      </div>
    </form>
  )
}

const StyledComponent = styled(Component)`
  color: ${styleTheme.colors.black};

  /* NOTE: gridを使うと〇〇Fieldのlabelとinputが何故か折り返してしまうためflexとする */
  display: flex;
  flex-direction: column;
  gap: 24px;
  @media (max-width: 751px) {
    gap: 12px;
  }

  > .item {
    .gender-wrapper {
      display: flex;
      gap: 62px;
      @media screen and (min-width: 768px) {
        margin-top: 12px;
      }
    }
  }

  .reservation-at__text-input {
    &.error {
      border-color: ${styleTheme.colors.red.base};
    }
  }

  > .agreement {
    line-height: 1;
    text-align: center;
    margin-bottom: 24px;
  }

  > .submit {
    text-align: center;
  }

  &__modal-date-change {
    display: flex;
    gap: 48px;
  }

  &__modal-contents {
    position: relative;
    padding: 32px;
    background-color: ${styleTheme.colors.white};
    height: calc(75vh - 64px);
    border-radius: 8px;
    box-shadow: 0 0 15px -5px ${styleTheme.colors.gray.base};

    > div {
      margin-bottom: 32px;
    }
  }
`
